<template>
  <div class="ai-chat-panel">
    <!-- 会话工具栏 -->
    <div class="conv-toolbar">
      <el-button text size="small" :icon="Plus" @click="newConversation">新对话</el-button>
      <el-button text size="small" :icon="Clock" @click="historyVisible = true">历史会话</el-button>
    </div>

    <!-- 消息列表 -->
    <div class="message-list" ref="listRef">
      <div v-if="messages.length === 0" class="empty-tip">
        <div class="empty-title">AI 助手</div>
        <div class="empty-desc">可以问我行业问题、语音吩咐办事，也可以发付款/票据截图让我帮你登记</div>
        <div class="quick-list">
          <div v-for="q in quickQuestions" :key="q" class="quick-item" @click="ask(q)">{{ q }}</div>
        </div>
      </div>

      <div v-for="(msg, index) in messages" :key="index" class="message-row" :class="msg.role">
        <div class="message-bubble" :class="{ error: msg.error }">
          <el-image
            v-if="msg.imageUrl"
            :src="msg.imageUrl"
            fit="cover"
            class="msg-image"
            :preview-src-list="[msg.imageUrl]"
            preview-teleported
          />
          <div class="message-content" v-html="renderContent(msg.content)"></div>
          <AiActionCard
            v-if="msg.action"
            :intent="msg.action.intent"
            :params="msg.action.params"
            :candidates="msg.action.candidates"
            :self="msg.action.self"
            :status="msg.action.status"
            :result-text="msg.action.resultText"
            :voucher-file="msg.action.voucherFile"
            @done="(text) => onActionDone(msg, text)"
            @cancel="onActionCancel(msg)"
          />
        </div>
      </div>

      <div v-if="loading" class="message-row assistant">
        <div class="message-bubble">
          <span class="typing">思考中...</span>
        </div>
      </div>
    </div>

    <!-- 已选票据图片预览 -->
    <div v-if="imageFile" class="image-bar">
      <el-image :src="imagePreviewUrl" fit="cover" class="thumb" />
      <span class="image-name">{{ imageFile.name }}</span>
      <span class="image-tip">将识别为收付款/报销票据</span>
      <el-icon class="remove" @click="clearImage"><Close /></el-icon>
    </div>

    <!-- 输入区 -->
    <div class="input-area">
      <el-upload
        :auto-upload="false"
        :show-file-list="false"
        accept="image/*"
        :on-change="onImageSelect"
        class="img-upload"
      >
        <el-button class="img-btn" :icon="Picture" circle :disabled="loading || recording" title="上传票据截图（付款/报销凭证）" />
      </el-upload>
      <el-button
        class="mic-btn"
        :type="recording ? 'danger' : 'default'"
        :icon="recording ? VideoPause : Microphone"
        circle
        :loading="recognizing"
        title="语音输入"
        @click="toggleRecord"
      />
      <el-input
        v-model="input"
        type="textarea"
        :rows="2"
        resize="none"
        :placeholder="recording ? '正在录音，点击红色按钮结束...' : (imageFile ? '可补充说明（如：这是王总付的定金），也可以直接发送' : '输入问题或指令，Enter 发送，Shift+Enter 换行')"
        @keydown.enter.exact.prevent="send"
        :disabled="loading || recording"
      />
      <el-button type="primary" class="send-btn" @click="send" :loading="loading" :disabled="(!input.trim() && !imageFile) || recording">
        发送
      </el-button>
    </div>

    <!-- 历史会话抽屉 -->
    <AiConversationHistory
      v-model="historyVisible"
      :current-id="conversationId"
      @select="onSelectConversation"
      @deleted="onDeletedConversation"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Microphone, VideoPause, Picture, Close, Plus, Clock } from '@element-plus/icons-vue'
import { parseAiCommand, parseAiVoucher, aiAsr, aiChat, type ChatMessage, type AiCommandResult } from '@/api/ai'
import { checkAiAvailable } from '@/utils/aiGate'
import AiActionCard from '@/components/ai/AiActionCard.vue'
import AiConversationHistory from '@/components/ai/AiConversationHistory.vue'

interface ActionState {
  intent: Exclude<AiCommandResult['intent'], 'none'>
  params: any
  candidates?: AiCommandResult['candidates']
  self?: AiCommandResult['self']
  status: 'pending' | 'done' | 'cancelled'
  resultText?: string
  voucherFile?: File // 票据原图，确认创建时上传为业务凭证
}

interface DisplayMessage extends ChatMessage {
  error?: boolean
  action?: ActionState
  imageUrl?: string
}

const messages = ref<DisplayMessage[]>([])
const input = ref('')
const loading = ref(false)
const listRef = ref<HTMLElement>()

// 落库会话（2026-11-04）：首轮问答后后端创建会话并返回 id，之后随消息携带，历史以库内为准
const conversationId = ref<number | null>(null)
const historyVisible = ref(false)

// 新对话：清空当前消息与会话 id
const newConversation = () => {
  messages.value = []
  conversationId.value = null
}

// 从历史会话载入（办事确认卡片等临时交互不落库，仅恢复问答消息）
const onSelectConversation = (id: number, msgs: ChatMessage[]) => {
  messages.value = msgs.map(m => ({ role: m.role, content: m.content }))
  conversationId.value = id
  scrollToBottom()
}

// 删除了正在查看的会话 → 回到新对话
const onDeletedConversation = (id: number) => {
  if (id === conversationId.value) newConversation()
}

const quickQuestions = [
  '提醒我明天上午给王总回电话',
  '帮我记录：今天拜访了张姐，她对客厅灯很满意',
  '30平米客厅需要多少流明？',
  '显色指数Ra90和Ra80区别大吗？'
]

// 简单 markdown 渲染：换行 + 加粗 + 列表（避免引入依赖）
const renderContent = (text: string) => {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^[-•]\s+(.+)$/gm, '<div class="li">• $1</div>')
    .replace(/\n/g, '<br>')
}

const scrollToBottom = async () => {
  await nextTick()
  if (listRef.value) {
    listRef.value.scrollTop = listRef.value.scrollHeight
  }
}

const ask = (question: string) => {
  input.value = question
  send()
}

// 从当前消息列表提取对话历史（多轮上下文）：只带纯文本问答，最近 10 条、单条截 1000 字（后端再兜底 2000）
const buildHistory = (): ChatMessage[] => {
  return messages.value
    .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.error && m.content)
    .slice(-10)
    .map(m => ({ role: m.role, content: m.content.slice(0, 1000) }))
}

// ===== 票据图片选择 =====
const imageFile = ref<File | null>(null)
const imagePreviewUrl = ref('')

const onImageSelect = (uploadFile: any) => {
  const raw = uploadFile?.raw as File | undefined
  if (!raw) return
  if (!raw.type.startsWith('image/')) {
    ElMessage.warning('只允许选择图片文件')
    return
  }
  if (raw.size > 8 * 1024 * 1024) {
    ElMessage.warning('图片不能超过 8MB')
    return
  }
  clearImage()
  imageFile.value = raw
  imagePreviewUrl.value = URL.createObjectURL(raw)
}

const clearImage = () => {
  if (imagePreviewUrl.value) URL.revokeObjectURL(imagePreviewUrl.value)
  imageFile.value = null
  imagePreviewUrl.value = ''
}

const send = async () => {
  const question = input.value.trim()
  const file = imageFile.value
  if ((!question && !file) || loading.value) return

  // 历史先取（当前这条还没入列表），供指令解析与普通问答理解上下文
  const history = buildHistory()

  messages.value.push({
    role: 'user',
    content: question || (file ? '识别这张票据' : ''),
    imageUrl: file ? imagePreviewUrl.value : undefined
  })
  input.value = ''
  // 预览 URL 已交给消息气泡使用，仅清除选择状态，不 revoke
  imageFile.value = null
  imagePreviewUrl.value = ''
  loading.value = true
  scrollToBottom()

  try {
    if (file) {
      // 票据识别：图片 + 可选文字说明 → 确认卡片（确认后才调用业务接口创建）
      const res = await parseAiVoucher(file, question)
      messages.value.push({
        role: 'assistant',
        content: res.reply || '识别完成，请核对以下信息：',
        action: {
          intent: res.intent,
          params: res.params,
          candidates: res.candidates,
          self: res.self,
          status: 'pending',
          voucherFile: file
        }
      })
    } else {
      const res = await parseAiCommand(question, history)
      if (res.intent === 'none') {
        // 普通问答：走通用对话通道（带历史上下文 + 数据查询工具 + 知识库），指令解析的 reply 作兜底
        // 已有落库会话时历史以库内为准（无需再传）；新会话首轮传内存历史，后端创建会话并返回 conversationId
        let reply = ''
        try {
          const chat = await aiChat(question, conversationId.value ? [] : history, conversationId.value)
          reply = chat.reply
          if (chat.conversationId) conversationId.value = chat.conversationId
        } catch {
          reply = res.reply
        }
        messages.value.push({ role: 'assistant', content: reply || res.reply || '（没有回复内容）' })
      } else {
        // 办事意图：回复 + 确认卡片（确认后才调用业务接口创建）
        messages.value.push({
          role: 'assistant',
          content: res.reply || '我理解了，请确认以下信息：',
          action: {
            intent: res.intent,
            params: res.params,
            candidates: res.candidates,
            self: res.self,
            status: 'pending'
          }
        })
      }
    }
  } catch (error: any) {
    messages.value.push({
      role: 'assistant',
      content: error?.message || 'AI 暂时不可用，请稍后重试',
      error: true
    })
  } finally {
    loading.value = false
    scrollToBottom()
  }
}

// 确认卡片：创建成功
const onActionDone = (msg: DisplayMessage, text: string) => {
  if (!msg.action) return
  msg.action.status = 'done'
  msg.action.resultText = text
  messages.value.push({ role: 'assistant', content: `${text}，还需要我做什么？` })
  scrollToBottom()
}

// 确认卡片：取消
const onActionCancel = (msg: DisplayMessage) => {
  if (!msg.action) return
  msg.action.status = 'cancelled'
  msg.action.resultText = '已取消'
}

// ===== 语音输入 =====
// 按钮常显（可发现性），点击录音前实时校验 ASR 配置状态，不可用则告知原因
const recording = ref(false)
const recognizing = ref(false)
let mediaRecorder: MediaRecorder | null = null
let audioChunks: Blob[] = []
let recordMime = ''

const toggleRecord = async () => {
  if (recording.value) {
    // 结束录音
    mediaRecorder?.stop()
    return
  }
  if (!(await checkAiAvailable({ featureName: '语音输入', needAsr: true }))) return
  // 开始录音
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    ElMessage.warning('当前浏览器不支持录音，请使用系统浏览器打开')
    return
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    recordMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : ''
    mediaRecorder = recordMime ? new MediaRecorder(stream, { mimeType: recordMime }) : new MediaRecorder(stream)
    audioChunks = []

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunks.push(e.data)
    }
    mediaRecorder.onstop = async () => {
      recording.value = false
      stream.getTracks().forEach(t => t.stop())
      const blob = new Blob(audioChunks, { type: mediaRecorder?.mimeType || recordMime || 'audio/webm' })
      if (blob.size === 0) return
      await recognizeAndSend(blob)
    }
    mediaRecorder.start()
    recording.value = true
  } catch (error: any) {
    ElMessage.warning(error?.name === 'NotAllowedError' ? '请允许使用麦克风' : '录音启动失败，请检查设备')
  }
}

// 浏览器录音（webm/mp4，44.1/48kHz）→ 16kHz 单声道 WAV
// 腾讯云一句话识别要求 16kHz 采样率且不支持 webm 封装，统一转码后 iOS/Android 都可用
const convertToWav16k = async (blob: Blob): Promise<Blob> => {
  const AudioCtx: any = window.AudioContext || (window as any).webkitAudioContext
  const arrayBuffer = await blob.arrayBuffer()
  const decodeCtx: AudioContext = new AudioCtx()
  let audioBuffer: AudioBuffer
  try {
    audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer)
  } finally {
    decodeCtx.close?.()
  }
  const sampleRate = 16000
  const frameCount = Math.max(1, Math.ceil(audioBuffer.duration * sampleRate))
  const offline = new OfflineAudioContext(1, frameCount, sampleRate)
  const source = offline.createBufferSource()
  source.buffer = audioBuffer
  source.connect(offline.destination)
  source.start(0)
  const rendered = await offline.startRendering()
  const samples = rendered.getChannelData(0)
  // Float32 → 16bit PCM
  const pcm = new Int16Array(samples.length)
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  // 拼 WAV 头（44字节）
  const buffer = new ArrayBuffer(44 + pcm.length * 2)
  const view = new DataView(buffer)
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
  }
  writeStr(0, 'RIFF')
  view.setUint32(4, 36 + pcm.length * 2, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM 块大小
  view.setUint16(20, 1, true) // PCM 格式
  view.setUint16(22, 1, true) // 单声道
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // 字节率
  view.setUint16(32, 2, true) // 块对齐
  view.setUint16(34, 16, true) // 位深
  writeStr(36, 'data')
  view.setUint32(40, pcm.length * 2, true)
  new Int16Array(buffer, 44).set(pcm)
  return new Blob([buffer], { type: 'audio/wav' })
}

// 录音 → 文字 → 自动走指令解析
const recognizeAndSend = async (blob: Blob) => {
  recognizing.value = true
  try {
    // 优先转码为 16kHz WAV（腾讯云要求）；浏览器不支持解码时回退原始格式（Whisper 可识别 webm/mp4）
    let uploadBlob = blob
    let filename = (blob.type || '').includes('mp4') ? 'voice.mp4' : 'voice.webm'
    try {
      uploadBlob = await convertToWav16k(blob)
      filename = 'voice.wav'
    } catch {
      // 保持原始格式上传
    }
    const res = await aiAsr(uploadBlob, filename)
    const text = (res.text || '').trim()
    if (!text) {
      ElMessage.warning('未识别到语音内容，请重试')
      return
    }
    input.value = text
    await send()
  } catch (error: any) {
    ElMessage.error(error?.message || '语音识别失败')
  } finally {
    recognizing.value = false
  }
}

// 清空对话（供父组件调用）
const clear = () => {
  newConversation()
}
defineExpose({ clear })
</script>

<style scoped>
.ai-chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f7fa;
}
.conv-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  padding: 4px 12px 0;
}
.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.empty-tip {
  text-align: center;
  padding: 32px 16px;
  color: #909399;
}
.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}
.empty-desc {
  font-size: 13px;
  margin-bottom: 20px;
}
.quick-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 320px;
  margin: 0 auto;
}
.quick-item {
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
}
.quick-item:hover {
  border-color: #409eff;
  color: #409eff;
}
.message-row {
  display: flex;
  margin-bottom: 12px;
}
.message-row.user {
  justify-content: flex-end;
}
.message-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  background: #fff;
  border: 1px solid #e4e7ed;
  color: #303133;
}
.message-row.user .message-bubble {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}
.message-bubble.error {
  background: #fef0f0;
  border-color: #fde2e2;
  color: #f56c6c;
}
.message-content :deep(.li) {
  padding-left: 4px;
}
.typing {
  color: #909399;
  animation: blink 1.2s infinite;
}
@keyframes blink {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
.msg-image {
  display: block;
  width: 160px;
  height: 100px;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: zoom-in;
}
.image-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
}
.image-bar .thumb {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  flex-shrink: 0;
}
.image-bar .image-name {
  font-size: 12px;
  color: #606266;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.image-bar .image-tip {
  flex: 1;
  font-size: 12px;
  color: #909399;
}
.image-bar .remove {
  cursor: pointer;
  color: #909399;
  flex-shrink: 0;
}
.image-bar .remove:hover {
  color: #f56c6c;
}
.input-area {
  display: flex;
  gap: 8px;
  padding: 12px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
  align-items: flex-end;
}
.img-upload {
  flex-shrink: 0;
}
.img-btn {
  flex-shrink: 0;
}
.mic-btn {
  flex-shrink: 0;
}
.send-btn {
  flex-shrink: 0;
}
</style>
